import { Suspense, useEffect, type ReactNode } from "react";
import { Link, Navigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Lock, RotateCw } from "lucide-react";
import FullScreenLoader from "@/components/feedback/full-screen-loader/FullScreenLoader";
import { Button } from "@/components/ui/button";
import {
  HR_KEYS,
  hrErrorData,
  hrErrorMessage,
  useHrMe,
} from "../api/hrApi";
import type { HrRequirement } from "../config/hr.nav";
import { HR_ICON_CHIP, HR_SURFACE, HR_TEXT } from "../config/hr.theme";
import { hrHoldsAny, useHrNav } from "../hooks/useHrNav";
import { DASH_GREEN } from "../ui/tokens";
import "../ui/hrSuite.css";

/**
 * HR's scroll container AND body tint, in one place so every screen —
 * including ones added later, and the guard's own states — gets both without
 * anyone remembering to.
 *
 * The height is explicit because nothing above it constrains one:
 * `SidebarInset` is `flex-1 flex-col` with no overflow, and `body` carries
 * `overflow: clip` (`styles/base/_base.scss`), so there is no document scroll
 * to fall back on. Without a bounded height the page simply grew past the
 * viewport and everything below the fold was unreachable. This is the same
 * measurement `.main` uses; HR does not reuse `.main`/`.main-body` because
 * their padding would double up with each page's own `p-6`.
 *
 * The three text tokens HR references aren't declared globally (see HR_TEXT),
 * and defining them at :root would shift CRM screens. So they sit on <body>
 * only while an HR screen is mounted: that reaches portalled dialogs and
 * menus, which a variable on this div never could.
 */
export function HrPageFrame({ children }: { children: ReactNode }) {
  useEffect(() => {
    const { style } = document.body;
    style.setProperty("--color-text-primary", HR_TEXT.primary);
    style.setProperty("--color-text-secondary", HR_TEXT.secondary);
    style.setProperty("--color-text-gray", HR_TEXT.gray);
    style.setProperty("--color-toggle", DASH_GREEN.brand);
    return () => {
      style.removeProperty("--color-text-primary");
      style.removeProperty("--color-text-secondary");
      style.removeProperty("--color-text-gray");
      style.removeProperty("--color-toggle");
    };
  }, []);
  return (
    <div
      className="flex flex-col overflow-y-auto"
      style={{
        height: "calc(var(--screen-h) - var(--header-height))",
        background: HR_SURFACE.page,
      }}
    >
      {children}
    </div>
  );
}

type GateState = "loading" | "off" | "error" | "ready";

/**
 * Where the suite stands for this user. `/me` answering 403 HR_DISABLED means
 * HR was switched off after the (cached) suites answer — treat it as off and
 * refresh that answer so the sidebar drops the suite too.
 */
function useHrGate(): {
  state: GateState;
  error: unknown;
  retry: () => void;
} {
  const nav = useHrNav();
  const meQuery = useHrMe();
  const queryClient = useQueryClient();
  const switchedOff =
    hrErrorData<{ code?: string }>(meQuery.error)?.code === "HR_DISABLED";

  useEffect(() => {
    if (switchedOff) {
      void queryClient.invalidateQueries({ queryKey: HR_KEYS.suites });
    }
  }, [switchedOff, queryClient]);

  const state: GateState = nav.isLoading
    ? "loading"
    : !nav.isHrEnabled || switchedOff
      ? "off"
      : meQuery.isError
        ? "error"
        : "ready";

  return { state, error: meQuery.error, retry: () => void meQuery.refetch() };
}

/** A calm in-page state — never a redirect, so it can never loop. */
function HrGateNotice({
  icon,
  title,
  body,
  action,
}: {
  icon: ReactNode;
  title: string;
  body: string;
  action: ReactNode;
}) {
  return (
    <div className="flex flex-1 items-start justify-center p-6">
      <div
        className="mt-16 flex w-full max-w-md flex-col items-center gap-3 rounded-2xl border bg-white px-8 py-10 text-center"
        style={{ borderColor: HR_SURFACE.border, boxShadow: HR_SURFACE.shadow }}
      >
        <span
          className="flex size-11 items-center justify-center rounded-full [&>svg]:size-5"
          style={{ background: HR_ICON_CHIP.bg, color: HR_ICON_CHIP.fg }}
        >
          {icon}
        </span>
        <p
          role="heading"
          aria-level={1}
          className="text-[18px] font-medium tracking-[-0.36px]"
          style={{ color: "var(--color-text-primary)" }}
        >
          {title}
        </p>
        <p
          className="text-[13px] leading-relaxed"
          style={{ color: "var(--color-text-gray)" }}
        >
          {body}
        </p>
        <div className="mt-2">{action}</div>
      </div>
    </div>
  );
}

/** "You don't have access" + a way to the first HR screen the user CAN open. */
function HrNoAccess() {
  const { canAccessHr, homePath, allowedItems } = useHrNav();
  const target = canAccessHr
    ? {
        to: homePath,
        label: `Go to ${
          allowedItems.find((i) => i.path === homePath)?.label ?? "HR"
        }`,
      }
    : { to: "/", label: "Back to Xale CRM" };

  return (
    <HrGateNotice
      icon={<Lock />}
      title="You don't have access to this part of HR"
      body="Your role doesn't include it. If you need it, ask an admin to update your role's HR permissions."
      action={
        <Button asChild variant="primary">
          <Link to={target.to} replace>
            {target.label}
          </Link>
        </Button>
      }
    />
  );
}

function HrAccessError({
  error,
  onRetry,
}: {
  error: unknown;
  onRetry: () => void;
}) {
  return (
    <HrGateNotice
      icon={<RotateCw />}
      title="We couldn't load your HR access"
      body={hrErrorMessage(error, "Something went wrong while checking what you can open in HR.")}
      action={
        <Button variant="primary" onClick={onRetry}>
          Try again
        </Button>
      }
    />
  );
}

/**
 * Client gate for one HR screen, keyed on the capabilities of its nav entry
 * (see `hrEntryRequirements` — the sidebar filters on the very same list).
 *
 *   loading          → loader
 *   HR not enabled   → back to the CRM home
 *   capability held  → the screen
 *   capability not   → an in-page "no access" state linking to the first HR
 *                      screen the user can open (or back to the CRM)
 *
 * Convenience only — every `/suites/hr/*` endpoint enforces the same
 * capability server-side, and that is the boundary that protects data.
 */
export default function HrRouteGuard({
  requires,
  children,
}: {
  requires: HrRequirement | readonly HrRequirement[];
  children: ReactNode;
}) {
  const gate = useHrGate();
  const { data: me } = useHrMe();

  if (gate.state === "loading") {
    return (
      <HrPageFrame>
        <FullScreenLoader />
      </HrPageFrame>
    );
  }
  if (gate.state === "off") return <Navigate to="/" replace />;
  if (gate.state === "error") {
    return (
      <HrPageFrame>
        <HrAccessError error={gate.error} onRetry={gate.retry} />
      </HrPageFrame>
    );
  }

  const list = "module" in requires ? [requires] : requires;
  if (!hrHoldsAny(me, list)) {
    return (
      <HrPageFrame>
        <HrNoAccess />
      </HrPageFrame>
    );
  }

  return (
    <HrPageFrame>
      <Suspense fallback={<FullScreenLoader />}>{children}</Suspense>
    </HrPageFrame>
  );
}

/**
 * `/hr` with no screen named — land on the first screen this user can open
 * (`useHrNav().homePath`). Same states as the guard, so a user with no HR
 * capability gets the calm notice instead of a bounce.
 */
export function HrHomeRedirect() {
  const gate = useHrGate();
  const { canAccessHr, homePath } = useHrNav();

  if (gate.state === "loading") {
    return (
      <HrPageFrame>
        <FullScreenLoader />
      </HrPageFrame>
    );
  }
  if (gate.state === "off") return <Navigate to="/" replace />;
  if (gate.state === "error") {
    return (
      <HrPageFrame>
        <HrAccessError error={gate.error} onRetry={gate.retry} />
      </HrPageFrame>
    );
  }
  if (canAccessHr) return <Navigate to={homePath} replace />;
  return (
    <HrPageFrame>
      <HrNoAccess />
    </HrPageFrame>
  );
}
